import 'client-only';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { type Brand } from '@/consts/brands';
import COOKIE_KEYS from '@/consts/cookieKeys';
import { INELIGIBILITY_REASONS } from '@/consts/eligibility';
import {
  TreatmentCategoryIds,
  type TreatmentCategory,
} from '@/consts/treatmentCategories';
import { type IneligibilityReasonType } from '@/types/eligibility/eligibility';
import { trackEvent, AnalyticsEvent } from '@/utils/analytics';
import cookieStorage from '@/utils/cookies/client';
import { canonicalPath } from '@/utils/metrics/pages';
import { formatDateString } from '@/utils/strings/formatDateString';
import { getDisplayName } from '@/utils/strings/getDisplayName';

const trackEligibilitySubmitted = (
  brand: Brand,
  treatmentCategory: TreatmentCategory,
) => {
  trackEvent(AnalyticsEvent.ELIGIBILITY_SUBMITTED, {
    path: canonicalPath(),
    brand,
    treatmentCategories: treatmentCategory,
    treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
    funnelId: cookieStorage.get(COOKIE_KEYS.funnelId),
  });
};

const useTrackEligibility = () => {
  const { brand, treatmentCategory } = useFlowContext();
  const trackEligibilitySubmission = () => {
    trackEligibilitySubmitted(brand, treatmentCategory!);
  };

  const trackProductEligibilitySubmission = (
    brand: Brand,
    ineligibleReason: string,
  ) => {
    trackEvent(AnalyticsEvent.PRODUCT_ELIGIBILITY_SUBMITTED, {
      brand,
      treatmentCategories: treatmentCategory!,
      treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
      funnelId: cookieStorage.get(COOKIE_KEYS.funnelId) ?? '',
      ineligibilityReason: ineligibleReason,
    });
  };

  const trackIneligibility = (
    brand: Brand,
    reason: IneligibilityReasonType,
    {
      birthdate,
    }: { state?: string; birthdate?: Date; termsAgreement?: boolean },
  ) => {
    const generalHairMessage = 'User cannot receive hair product';
    const INELIGIBILITY_EVENT_REASONS: {
      [key in IneligibilityReasonType]: string;
    } = {
      [INELIGIBILITY_REASONS.AGE]: `${getDisplayName(
        brand,
      )} user under minimum age`,
      [INELIGIBILITY_REASONS.ALREADY_COMPLETED_SIGN_UP]:
        'Already has completed signup for this treatment category.',
      [INELIGIBILITY_REASONS.STATE]: 'State not available',
      [INELIGIBILITY_REASONS.OBGYN]: generalHairMessage,
      [INELIGIBILITY_REASONS.HAIR_CANNOT_RECEIVE_PRODUCT]: generalHairMessage,
      [INELIGIBILITY_REASONS.HAIR_LOSS_LOCATION]: generalHairMessage,
      [INELIGIBILITY_REASONS.HAIR_AGE]: 'User under minimum hair age',
      [INELIGIBILITY_REASONS.HAIR_UNDELIVERABLE_ADDRESS]:
        'Cannot deliver to user address',
      [INELIGIBILITY_REASONS.SHOULD_SHOW_NEW_SIGNUP_FLOW]:
        'User should use new signup survey instead of new treatment survey',
      [INELIGIBILITY_REASONS.UNKNOWN]: 'Unknown ineligibility reason',
      [INELIGIBILITY_REASONS.NONE]: 'NONE',
    };

    if (reason === INELIGIBILITY_REASONS.NONE) {
      return;
    }

    const dob = formatDateString(birthdate);
    const isIneligibleAge = reason === INELIGIBILITY_REASONS.AGE;

    trackEvent(AnalyticsEvent.ELIGIBILITY_FAILED, {
      reason: INELIGIBILITY_EVENT_REASONS[reason],
      treatmentCategories: treatmentCategory!,
      treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
      funnelId: cookieStorage.get(COOKIE_KEYS.funnelId) ?? '',
      ...(isIneligibleAge ? { dob } : {}),
    });
  };

  return {
    trackIneligibility,
    trackEligibilitySubmission,
    trackProductEligibilitySubmission,
  };
};

export { useTrackEligibility, trackEligibilitySubmitted };
